import apiClient from "@/api/apiClient";
import { type MoveDeviceProps } from "@/api/resources/devices/types";
import {
  createPaginatedSupabaseQuery,
  createSearchSupabaseQuery,
  spawnCreateApiFunction,
  spawnGetApiFunction,
  spawnUpdateApiFunction,
  type ApiResourceConfig,
  type SearchOptionFilters,
} from "@/utils/api";
import { type QueryPaginationFilters } from "@/utils/pagination";

const table = "devices";
export const query = `
                                      *,
                                      device_tags(*),
                                      connections_to_devices(*),
                                      manufacturer:manufacturers(*),
                                      model:device_models(*),
                                      search_m:manufacturers(name),
                                      search_dm:device_models(name),
                                      search_dt:device_tags(name)
                                      `;
export const apiConfigDevices: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["name", "ref", "software_version", "search_m.name", "search_dm.name", "search_dt.name"],
  sortables: ["name", "ref", "created_at", "updated_at", "last_auth", "manufacturer.name", "model.name"],
};

export const getDevice = spawnGetApiFunction({ table, query });
export const createDevice = spawnCreateApiFunction({ table, query });
export const updateDevice = spawnUpdateApiFunction({ table, query });

export const deleteDevice = async (_device_id: string) => {
  const { error } = await apiClient.rpc("soft_delete_device", {
    _device_id,
  });
  if (error) throw error;
};

export const moveDevice = async ({ deployment_id: _deployment_id, id: _device_id }: MoveDeviceProps) => {
  const { error } = await apiClient.rpc("move_device", {
    _deployment_id,
    _device_id,
  });
  if (error) throw error;
};

export async function getPaginatedDevices({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigDevices.searchables,
      sortables: apiConfigDevices.sortables,
      filters: [
        ["deleted_at", "is", null], // filter out soft-deleted devices
      ],
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });

  if (error) throw error;
  return { data, count };
}

export async function getDevicesBySearch({
  searchOptions,
  customFilters,
  customOrFilters,
  limit,
}: SearchOptionFilters) {
  const { data, count, error } = await createSearchSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigDevices.searchables,
      sortables: apiConfigDevices.sortables,
      filters: [
        ["deleted_at", "is", null], // filter out soft-deleted devices
      ],
    },
    searchOptions,
    customFilters,
    customOrFilters,
    limit,
  });

  if (error) throw error;
  return { data, count };
}
